import { Layout } from '@/components/layout/Layout';
import { motion } from 'framer-motion';

export default function Privacy() {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                NexQR ("we", "our", or "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard
                your information when you use our QR code generation and management
                service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-medium mb-2">Personal Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Email address (for account creation)</li>
                <li>Name (optional, for profile)</li>
                <li>Payment information (processed securely by Stripe/Razorpay)</li>
              </ul>
              
              <h3 className="text-xl font-medium mb-2 mt-4">Usage Data</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>QR code scan analytics (IP address, device type, location)</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>To provide and maintain our service</li>
                <li>To process your transactions</li>
                <li>To send you service-related communications</li>
                <li>To provide QR code scan analytics</li>
                <li>To detect and prevent fraud</li>
                <li>To improve our service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal data only for as long as necessary to fulfill
                the purposes outlined in this policy. You can configure data retention
                settings in your account settings. Default retention period is 365 days
                for scan analytics data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Your Rights (GDPR)</h2>
              <p className="text-muted-foreground mb-4">
                If you are a resident of the European Economic Area (EEA), you have
                certain data protection rights:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restrict Processing:</strong> Request restriction of processing</li>
                <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
                <li><strong>Right to Object:</strong> Object to our processing of your data</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, visit your account settings or contact us at
                privacy@nexqr.online
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. California Privacy Rights (CCPA)</h2>
              <p className="text-muted-foreground mb-4">
                California residents have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether personal information is sold or disclosed</li>
                <li>Right to say no to the sale of personal information</li>
                <li>Right to access your personal information</li>
                <li>Right to request deletion of personal information</li>
                <li>Right to equal service and price</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                <strong>We do not sell your personal information.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies and similar tracking technologies to track activity on
                our service. You can control cookie preferences through our cookie
                consent banner or your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement appropriate security measures including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>SSL/TLS encryption for data in transit</li>
                <li>Encrypted storage for sensitive data</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
                <li>Password hashing using bcrypt</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Third-Party Services</h2>
              <p className="text-muted-foreground mb-4">
                We use the following third-party services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Supabase:</strong> Database and authentication</li>
                <li><strong>Stripe/Razorpay:</strong> Payment processing</li>
                <li><strong>IP-API:</strong> Geolocation for analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <ul className="list-none space-y-2 text-muted-foreground mt-4">
                <li>Email: privacy@nexqr.online</li>
                <li>Data Protection Officer: dpo@nexqr.online</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
